import Lock from '../helpers/lock';
import { redirect } from '../helpers/url';
import { ResponseState } from '../interfaces/api-response';
export var OrderRowType;
(function (OrderRowType) {
    OrderRowType["Product"] = "product";
    OrderRowType["Openset"] = "openset";
    OrderRowType["Voucher"] = "voucher";
})(OrderRowType || (OrderRowType = {}));
export default class Cart {
    static async add(sku, quantity, options, voucher) {
        if (typeof quantity === 'undefined') {
            quantity = 1;
        }
        if (typeof options === 'undefined') {
            options = {};
        }
        let { productAsGift, gift, fromCart } = options;
        if (typeof productAsGift === 'undefined') {
            productAsGift = false;
        }
        if (typeof gift === 'undefined') {
            gift = false;
        }
        if (typeof fromCart === 'undefined') {
            fromCart = false;
        }
        let url = `/cart/add/modal/false/sku/${encodeURIComponent(sku)}/qty/${encodeURIComponent(quantity)}`;
        if (productAsGift) {
            url += '/f_prodgift/Y?csrc=product';
        }
        if (gift) {
            url += '/f_gift/Y';
        }
        if (fromCart) {
            url += '/fromCart/1';
        }
        const response = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            cache: 'no-cache',
            body: JSON.stringify({
                voucher: voucher,
            }),
        });
        if (!response.ok) {
            throw new Error('Failed to add product to cart');
        }
        const body = (await response.json());
        if (body.success !== true) {
            if (typeof body.error !== 'undefined') {
                throw new Error(body.error);
            }
            else {
                throw new Error('Failed to add product to cart');
            }
        }
        // Zmienił się stan koszyka, zezwalamy na pobieranie wartości nie z cache
        await this.acquireLock();
        this.summary = null;
        this.releaseLock();
        if (typeof body.current_quantity !== 'undefined') {
            this.triggerListeners({
                name: 'cartUpdate',
                details: {
                    sku: sku,
                    quantity: body.current_quantity,
                    addedQuantity: quantity,
                },
            });
        }
        return body;
    }
    static async addDiscountCode(discountCode) {
        const response = await fetch('/cart/api/discount-code', {
            method: 'POST',
            credentials: 'include',
            cache: 'no-cache',
            body: JSON.stringify({ code: discountCode }),
        });
        if (!response.ok) {
            throw new Error('Failed to set discount code in cart');
        }
        const body = (await response.json());
        await this.acquireLock();
        this.summary = null;
        this.releaseLock();
        // Wymuszenie przeładowania podsumowania
        this.triggerListeners({ name: 'discountCodeAdded', details: {} });
        return body;
    }
    /** Dodaj do wierszy koszyka voucher w celu jego zakupu */
    static async addVoucher(voucher) {
        // Dodaj voucher do koszyka
        let response;
        try {
            response = await fetch(`/api/cart/session/vouchers`, {
                method: 'POST',
                credentials: 'include',
                cache: 'no-cache',
                body: JSON.stringify(voucher),
            });
        }
        catch (error) {
            return {
                state: ResponseState.CommunicationFailure,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
        // W przypadku niepoprawnej odpowiedzi, zwracamy informację o błędach
        if (response.status === 400) {
            return { state: ResponseState.Rejected, payload: (await response.json()) };
        }
        else if (response.status !== 201) {
            return {
                state: ResponseState.CommunicationFailure,
                error: new Error(`Received invalid response code ${response.status}`),
            };
        }
        // Zmienił się stan koszyka, zezwalamy na pobieranie wartości nie z cache
        await this.acquireLock();
        this.summary = null;
        this.releaseLock();
        // Wymuszenie przeładowania podsumowania
        this.triggerListeners({ name: 'voucherAdded', details: {} });
        return { state: ResponseState.Success, payload: {} };
    }
    /** Wygeneruj podgląd vouchera */
    static async previewVoucher(voucher) {
        let response;
        try {
            response = await fetch(`/api/cart/session/vouchers/preview`, {
                method: 'POST',
                credentials: 'include',
                cache: 'no-cache',
                body: JSON.stringify(voucher),
            });
        }
        catch (error) {
            return {
                state: ResponseState.CommunicationFailure,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
        // W przypadku niepoprawnej odpowiedzi, zwracamy informację o błędach
        if (response.status === 429) {
            // API przeciążone, próbujemy ponownie po czasie wskazanym w nagłówku Retry-After
            const retryAfter = response.headers.get('Retry-After');
            if (retryAfter === null) {
                return {
                    state: ResponseState.CommunicationFailure,
                    error: new Error('Received 429 response without Retry-After header'),
                };
            }
            const retryAfterSeconds = parseInt(retryAfter, 10);
            if (isNaN(retryAfterSeconds)) {
                return {
                    state: ResponseState.CommunicationFailure,
                    error: new Error('Received 429 response with invalid Retry-After header'),
                };
            }
            await new Promise((resolve) => setTimeout(resolve, retryAfterSeconds * 1000));
            return await this.previewVoucher(voucher);
        }
        else if (response.status === 400) {
            return { state: ResponseState.Rejected, payload: (await response.json()) };
        }
        else if (response.status !== 200) {
            return {
                state: ResponseState.CommunicationFailure,
                error: new Error(`Received invalid response code ${response.status}`),
            };
        }
        try {
            return { state: ResponseState.Success, payload: (await response.json()) };
        }
        catch (error) {
            return {
                state: ResponseState.CommunicationFailure,
                error: error instanceof Error ? error : new Error('Unknown error'),
            };
        }
    }
    /** Usuń wiersz z koszyka */
    static async remove(rowID) {
        // Wykonaj żądanie usunięcia wiersza
        const response = await fetch(`/api/cart/session/products/${encodeURIComponent(rowID)}`, {
            method: 'DELETE',
            credentials: 'include',
            cache: 'no-cache',
        });
        if (!response.ok) {
            throw new Error('Failed to remove product from cart');
        }
        // Zmienił się stan koszyka, zezwalamy na pobieranie wartości nie z cache
        await this.acquireLock();
        this.summary = null;
        this.releaseLock();
        // Wymuszenie przeładowania podsumowania
        this.triggerListeners({ name: 'rowRemoved', details: {} });
    }
    static async selectGift(ruleId, actionIndex, multiplier, index) {
        await this.acquireLock();
        this.summary = null;
        this.releaseLock();
        let url = '/cart/selectgift';
        url +=
            '/r/' +
                encodeURIComponent(ruleId) +
                '/a/' +
                encodeURIComponent(actionIndex) +
                '/m/' +
                encodeURIComponent(multiplier) +
                '/n/' +
                encodeURIComponent(index);
        redirect(url);
    }
    /**
     * Pobierz ostatni zapamiętany stan koszyka (zwrócony przez API)
     */
    static getLastSummary() {
        try {
            const data = localStorage.getItem('cart_state');
            if (data === null) {
                return null;
            }
            return JSON.parse(data);
        }
        catch (e) {
            console.error(e);
            return null;
        }
    }
    static async getSummary() {
        // Tylko jeden proces jednocześnie może uzyskiwać dostęp do ilości w koszyku
        await this.acquireLock();
        if (this.summary !== null) {
            this.releaseLock();
            return this.summary;
        }
        // Wczytanie z API
        try {
            this.summary = await this.getSummaryFromApi();
        }
        catch (e) {
            this.releaseLock();
            throw e;
        }
        // Zapamiętanie stanu
        localStorage.setItem('cart_state', JSON.stringify(this.summary));
        if (this.summary.promotions.length > 0) {
            this.triggerListeners({ name: 'promotionsChanged', details: {} });
        }
        // Zwolnienie blokady
        this.releaseLock();
        return this.summary;
    }
    static async getPromotionForSKU(sku) {
        const summary = await this.getSummary();
        const eligiblePromotions = [];
        if (summary && summary.promotions) {
            summary.promotions.forEach((promotion) => {
                promotion.actions.forEach((action) => {
                    if (action.eligible_skus === null || action.eligible_skus.length === 0) {
                        if (action.type === 'product_as_gift') {
                            eligiblePromotions.push(promotion);
                        }
                        return;
                    }
                    action.eligible_skus.forEach((eligibleSku) => {
                        if (sku === eligibleSku || (typeof eligibleSku === 'object' && eligibleSku.sku === sku)) {
                            eligiblePromotions.push(promotion);
                        }
                    });
                });
            });
        }
        return eligiblePromotions;
    }
    static async getSummaryFromApi() {
        const response = await fetch('/api/v1/cart/summary', {
            method: 'GET',
            cache: 'no-cache',
        });
        if (!response.ok) {
            throw new Error('Failed to get cart summary');
        }
        return (await response.json());
    }
    static getLockHandler() {
        if (this.lockHandler === null) {
            this.lockHandler = new Lock();
        }
        return this.lockHandler;
    }
    static acquireLock() {
        return this.getLockHandler().acquireLock();
    }
    static releaseLock() {
        this.getLockHandler().releaseLock();
    }
    /**
     * Dodaj funkcję nasłuchującą na zmianę stanu produktu w koszyku
     * @param fn Funkcja wykonywana w momencie zaktualizowania ilości produktu w koszyku
     */
    static addListener(fn) {
        this.listeners.push(fn);
    }
    /**
     * Zakomunikuj nasłuchującym elementom o zmianie stanu koszyka
     * @param details SKU produktu ze zmienionym stanem w koszyku
     */
    static triggerListeners(event) {
        this.listeners.forEach((fn) => {
            fn(event);
        });
    }
}
Cart.lockHandler = null;
Cart.summary = null;
Cart.listeners = [];
